/**
 * lead-importer.ts
 * Client-side helpers for OS Bot lead import from text and URLs.
 */

import { supabase } from '../supabase';

export interface ParsedLeadData {
  name?: string;
  phone?: string;
  email?: string;
  propertyAddress?: string;
  city?: string;
  state?: string;
  zip?: string;
  estimatedValue?: number;
  bedrooms?: number;
  bathrooms?: number;
  sqft?: number;
  propertyType?: string;
  agentName?: string;
  agentPhone?: string;
  notes?: string;
}

/**
 * Detects if a string is a URL (http/https or starts with www.)
 */
export function isUrl(text: string): boolean {
  const trimmed = text.trim();
  return /^(https?:\/\/|www\.)\S+/i.test(trimmed);
}

/**
 * Normalizes a URL by prepending https:// if missing
 */
export function normalizeUrl(raw: string): string {
  const trimmed = raw.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return 'https://' + trimmed;
}

/**
 * Scrapes a property URL via the scrape-property edge function.
 */
export async function scrapePropertyUrl(url: string): Promise<{
  success: boolean;
  data?: ParsedLeadData;
  rawData?: Record<string, unknown>;
  errorMsg?: string;
}> {
  if (!supabase) return { success: false, errorMsg: 'Supabase not configured' };

  try {
    const normalizedUrl = normalizeUrl(url);
    const { data, error } = await supabase.functions.invoke('scrape-property', {
      body: { url: normalizedUrl },
    });

    if (error || data?.errorCode) {
      const code = data?.errorCode || '';
      let msg = 'Scraping failed';
      if (code === 'SITE_BLOCKED') msg = `This site blocked automated access. Try copying the property details and pasting them instead.`;
      else if (code === 'TIMEOUT') msg = 'The site took too long to respond. Try pasting the details manually.';
      else if (code === 'NOT_FOUND') msg = 'Page not found (404). Double-check the URL.';
      else if (code === 'NO_CONTENT') msg = 'Page returned empty content — may require JavaScript. Paste the details instead.';
      else if (data?.error) msg = data.error;
      else if (error?.message) msg = error.message;
      return { success: false, errorMsg: msg };
    }

    if (!data || (!data.address && !data.price && !data.bedrooms)) {
      return { success: false, errorMsg: 'Could not extract property data. Try pasting the property details instead.' };
    }

    const parsed: ParsedLeadData = {
      propertyAddress: data.address || '',
      city: data.city || '',
      state: data.state || '',
      zip: data.zip || '',
      estimatedValue: data.price || 0,
      bedrooms: data.bedrooms || 0,
      bathrooms: data.bathrooms || 0,
      sqft: data.sqft || 0,
      agentName: data.listingAgent || '',
      agentPhone: data.agentPhone || '',
      notes: `Imported from ${data.source || 'URL'}. ${normalizedUrl}`,
    };

    return { success: true, data: parsed, rawData: data };
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'Unknown error';
    return { success: false, errorMsg: msg };
  }
}

/**
 * Parses raw pasted text to extract lead fields using regex heuristics.
 * Example: "John Smith, 5697 Pine Rd, Thomasville PA 17364, 717-555-1234, 3 bed 2 bath, asking $250k"
 */
export function parseLeadFromText(text: string): ParsedLeadData {
  const result: ParsedLeadData = {};
  const cleaned = text.replace(/\r\n/g, '\n').replace(/\t/g, ' ');

  // Phone
  const phoneMatch = cleaned.match(/\b(\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4})\b/);
  if (phoneMatch) {
    const digits = phoneMatch[1].replace(/\D/g, '');
    result.phone = `${digits.slice(0,3)}-${digits.slice(3,6)}-${digits.slice(6)}`;
  }

  // Email
  const emailMatch = cleaned.match(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/);
  if (emailMatch) result.email = emailMatch[0];

  // Price — handle $250k, $1.2M, $250,000
  const priceMatch = cleaned.match(/\$\s*([\d,]+(?:\.\d+)?)\s*([kKmM]?)/);
  if (priceMatch) {
    let val = parseFloat(priceMatch[1].replace(/,/g, ''));
    const suffix = priceMatch[2].toLowerCase();
    if (suffix === 'k') val *= 1000;
    if (suffix === 'm') val *= 1000000;
    result.estimatedValue = Math.round(val);
  }

  // Beds
  const bedsMatch = cleaned.match(/\b(\d+)\s*(?:bed(?:room)?s?|bd)\b/i);
  if (bedsMatch) result.bedrooms = parseInt(bedsMatch[1]);

  // Baths
  const bathsMatch = cleaned.match(/\b(\d+(?:\.\d+)?)\s*(?:bath(?:room)?s?|ba)\b/i);
  if (bathsMatch) result.bathrooms = parseFloat(bathsMatch[1]);

  // Sqft
  const sqftMatch = cleaned.match(/\b([\d,]+)\s*(?:sq\.?\s*ft\.?|sqft|square\s*feet)\b/i);
  if (sqftMatch) result.sqft = parseInt(sqftMatch[1].replace(/,/g, ''));

  // Property type
  if (/multi[\s-]?family/i.test(cleaned)) result.propertyType = 'multi-family';
  else if (/commercial|retail/i.test(cleaned)) result.propertyType = 'commercial';
  else if (/condo|apartment/i.test(cleaned)) result.propertyType = 'condo';
  else if (/land|lot|acres?/i.test(cleaned)) result.propertyType = 'land';
  else if (/mobile|manufactured/i.test(cleaned)) result.propertyType = 'mobile';

  // Address — look for street number + street name
  const addrMatch = cleaned.match(
    /\b(\d{1,6}\s+[A-Za-z][A-Za-z0-9 .]{2,40}(?:St(?:reet)?|Ave(?:nue)?|Rd|Road|Blvd|Boulevard|Dr(?:ive)?|Ln|Lane|Ct|Court|Way|Pl|Place|Pkwy|Hwy|Cir|Circle|Loop|Trail|Trl|Hwy|Hwy\s+\d+|Hwy\s+[A-Z])[.,]?\s*(?:[A-Za-z ]{2,25}[,\s]+)?(?:[A-Z]{2}\s+\d{5})?)/i
  );
  if (addrMatch) {
    result.propertyAddress = addrMatch[1].replace(/[,\s]+$/, '').trim();
  } else {
    const looseAddr = cleaned.match(/\b\d+\s+[A-Za-z][A-Za-z0-9 ]{3,40}\b/);
    if (looseAddr) result.propertyAddress = looseAddr[0].trim();
  }

  // State + Zip
  const stateZipMatch = cleaned.match(/\b([A-Z]{2})\s+(\d{5}(?:-\d{4})?)\b/);
  if (stateZipMatch) {
    result.state = stateZipMatch[1];
    result.zip = stateZipMatch[2];
  }

  // Name — first line/segment that looks like "Firstname Lastname"
  const segments = cleaned.split(/[\n,;]+/);
  for (const seg of segments) {
    const trimSeg = seg.trim();
    if (/\d{3}[-.\s]\d{3}|\$|\d{5}|\d+\s+(St|Rd|Ave|Blvd|Dr|Ln|Ct)/i.test(trimSeg)) continue;
    const nameMatch = trimSeg.match(/^([A-Z][a-z]+(?:\s+[A-Z][a-z]+){1,2})$/);
    if (nameMatch && !/Status|Lead|Address|Phone|Email|Notes|Price|Beds|Baths|Sqft/i.test(nameMatch[1])) { result.name = nameMatch[1]; break; }
  }

  // Notes — asking price context, other descriptors
  const noteMatches = cleaned.match(/(?:asking|listed at|notes?|comment|details?|desc(?:ription)?)[:\s]+([^\n.]{5,100})/gi);
  if (noteMatches) result.notes = noteMatches.map(m => m.trim()).join('. ');

  return result;
}

/**
 * Formats a ParsedLeadData into a human-readable summary for OS Bot confirmation.
 */
export function formatLeadSummary(lead: ParsedLeadData, missing: string[]): string {
  const lines: string[] = ['**📋 Lead Information Found:**\n'];
  if (lead.name) lines.push(`• **Name:** ${lead.name}`);
  if (lead.propertyAddress) {
    let addr = lead.propertyAddress;
    if (lead.city) addr += `, ${lead.city}`;
    if (lead.state) addr += ` ${lead.state}`;
    if (lead.zip) addr += ` ${lead.zip}`;
    lines.push(`• **Address:** ${addr}`);
  }
  if (lead.phone) lines.push(`• **Phone:** ${lead.phone}`);
  if (lead.email) lines.push(`• **Email:** ${lead.email}`);
  if (lead.estimatedValue) lines.push(`• **Price:** $${lead.estimatedValue.toLocaleString()}`);
  if (lead.bedrooms || lead.bathrooms) lines.push(`• **Beds/Baths:** ${lead.bedrooms || '?'} bed / ${lead.bathrooms || '?'} bath`);
  if (lead.sqft) lines.push(`• **Sqft:** ${lead.sqft.toLocaleString()}`);
  if (lead.propertyType) lines.push(`• **Type:** ${lead.propertyType}`);
  if (lead.agentName) lines.push(`• **Agent:** ${lead.agentName}${lead.agentPhone ? ` (${lead.agentPhone})` : ''}`);
  if (lead.notes) lines.push(`• **Notes:** ${lead.notes}`);

  if (missing.length > 0) lines.push(`\n⚠️ **Missing:** ${missing.join(', ')}`);
  lines.push('\nReply **"yes"** to create this lead or **"no"** to cancel.');
  return lines.join('\n');
}

/**
 * Returns a list of important missing fields for a parsed lead.
 */
export function getMissingFields(lead: ParsedLeadData): string[] {
  const missing: string[] = [];
  if (!lead.name) missing.push('owner name');
  if (!lead.propertyAddress) missing.push('property address');
  if (!lead.phone && !lead.email) missing.push('contact info');
  return missing;
}
